<template>
  <div
    ref="$el"
    class="text-iframe"
  >
    <div class="text">
      <UiComponentHeading
        as="paragraph"
        :level="level"
      >
        <slot name="heading" />
      </UiComponentHeading>
      <UiComponentParagraph class="description">
        <slot name="paragraph" />
      </UiComponentParagraph>
    </div>
    <div class="iframe">
      <iframe
        v-if="isVisible"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
        frameborder="0"
        height="100%"
        loading="lazy"
        :src="`https://www.youtube.com/embed/${video.providerUid}`"
        title="YouTube video player"
        width="100%"
      />
    </div>
  </div>
</template>

<script setup>
// Props
const props = defineProps({
  level: {
    default() {
      return 3
    },
    required: false,
    type: Number,
  },
  video: {
    required: true,
    type: Object,
  },
})

// Refs
const $el = ref(null)
const isVisible = ref(false)
const observer = ref(false)

// Methods
function observe() {
  observer.value = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      isVisible.value = true
      unobserve()
    }
  })
  observer.value.observe($el.value)
}

function unobserve() {
  observer.value.disconnect()
  observer.value = null
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  if (observer.value) {
    unobserve()
  }
})

onMounted(() => {
  observe()
})
</script>

<style lang="postcss" scoped>
.text-iframe {
  @apply flex flex-col w-full;

  @screen lg {
    @apply flex-row-reverse items-center;
  }

  .iframe,
  .text {
    @apply w-full;
  }

  .iframe {
    @apply relative mt-6;
    padding-bottom: 56.25%;

    @screen md {
      @apply max-w-2xl;
    }

    @screen lg {
      @apply mt-0 max-w-full;
      padding-bottom: 28.125%;
    }

    iframe {
      @apply absolute top-0 left-0 w-full h-full;
    }
  }

  .text {
    @screen lg {
      @apply pl-28;
    }

    .h3 {
      @apply font-medium text-2xl text-winter-green-900 leading-8 !important;

      @screen mdminus {
        font-size: 28px !important;
        line-height: 34px !important;
      }

      @screen mdplus {
        @apply text-3xl leading-10 !important;
      }
    }

    .h4 {
      @apply font-medium text-winter-green-900 text-xl leading-7 !important;

      @screen mdminus {
        @apply text-2xl leading-8 !important;
      }

      @screen mdplus {
        @apply text-2xl leading-8 !important;
      }
    }

    .description {
      @apply mt-4 text-grey-blue-600;
    }
  }
}
</style>
